import { useMemo } from 'react';
import { useFilteredNavigation } from './useFilteredNavigation';
import { useManagementNavigation } from './useManagementNavigation';
import type { UserContext } from '../types';

export interface NavPage {
  label: string;
  path: string;
  group: string;
}

/**
 * The user's fully-resolved, RBAC- and entitlement-filtered navigation —
 * the SAME registry groups + management sections that render the sidebar and
 * command palette — flattened to { label, path, group }.
 *
 * This is the single source of truth for "what screens can THIS user reach",
 * so anything that needs it (e.g. Dana's navigation guidance) stays accurate
 * per-tenant/per-permission and never drifts when features ship.
 */
export function useResolvedNavPages(userContext: UserContext): NavPage[] {
  const { navigationGroups } = useFilteredNavigation({ userContext });
  const { adminItems, payrollItems, attendanceItems, showAdmin, showPayroll, showAttendance } =
    useManagementNavigation();

  return useMemo(() => {
    const out: NavPage[] = [];
    navigationGroups.forEach((g) => {
      g.items.forEach((i) => {
        out.push({ label: i.label, path: i.path, group: g.label });
        (i.children || []).forEach((c: any) =>
          out.push({ label: c.label, path: c.path, group: i.label }),
        );
      });
    });
    // The RBAC-gated manager/admin sections (same gates as the sidebar).
    if (showAdmin) adminItems.forEach((i) => out.push({ label: i.label, path: i.path, group: 'Administration' }));
    if (showPayroll) payrollItems.forEach((i) => out.push({ label: i.label, path: i.path, group: 'Payroll' }));
    if (showAttendance) attendanceItems.forEach((i) => out.push({ label: i.label, path: i.path, group: 'Attendance' }));
    // Dedup by path.
    const seen = new Set<string>();
    return out.filter((p) => (p.path && !seen.has(p.path) ? (seen.add(p.path), true) : false));
  }, [navigationGroups, adminItems, payrollItems, attendanceItems, showAdmin, showPayroll, showAttendance]);
}
